import { ModuleDocContent } from "./docsData";

export const dashboardModuleDocs: Record<string, ModuleDocContent> = {
  "admin-dashboard": {
    title: "Admin Dashboard",
    subtitle: "Platform Overview & Quick Actions",
    color: "blue",
    badge: "Dashboard",
    description:
      "Your central command center for monitoring platform activity, key metrics, department performance, and real-time learning events across the organization.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Admin Dashboard</strong> is your landing page after login, providing a real-time snapshot of your organization's learning ecosystem. It displays key metrics, department-level performance charts, activity heatmaps, and a live activity timeline — all filterable by time range.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b909c5c9-3ee0-41da-8e8b-9586ba1752a4.png" alt="Admin Dashboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Admin Dashboard — Learning Intelligence Hub</p></div>

      <section>
        <h2>Time Range Filter (Granularity)</h2>
        <p>At the top of the dashboard, a set of time range buttons lets you control the period for all metrics and charts displayed below.</p>
        <table>
          <thead>
            <tr>
              <th>Option</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Week</strong></td>
              <td>Shows data for the current week. Metrics compare against the previous week.</td>
            </tr>
            <tr>
              <td><strong>Month</strong></td>
              <td>Shows data for the current month (default). Metrics compare against the previous month.</td>
            </tr>
            <tr>
              <td><strong>Quarter</strong></td>
              <td>Shows data for the current quarter. Metrics compare against the previous quarter.</td>
            </tr>
            <tr>
              <td><strong>Year</strong></td>
              <td>Shows data for the current year. Metrics compare against the previous year.</td>
            </tr>
            <tr>
              <td><strong>Custom</strong></td>
              <td>Pick a custom date range using <strong>From</strong> and <strong>To</strong> date pickers. The number of days in the selected range is displayed alongside.</td>
            </tr>
          </tbody>
        </table>
        <div class="sz-callout sz-callout-tip">
          <div class="sz-callout-label">Tip</div>
          <p>The selected time range is saved in the URL, so you can share a filtered dashboard view by copying the page link.</p>
        </div>
      </section>

      <section>
        <h2>Stats Cards</h2>
        <p>Four KPI summary cards are displayed at the top, each showing the current value, period-over-period change, and a trend indicator (up/down/stable).</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>What it shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Employees</strong></td>
              <td>Count of active users in the platform for the selected period, with change from the previous period.</td>
            </tr>
            <tr>
              <td><strong>Departments</strong></td>
              <td>Number of active departments, with change from the previous period.</td>
            </tr>
            <tr>
              <td><strong>Active Courses</strong></td>
              <td>Number of currently assigned courses, with change from the previous period.</td>
            </tr>
            <tr>
              <td><strong>Completion Rate</strong></td>
              <td>Organization-wide average completion rate as a percentage, with percentage-point change from the previous period.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Department Performance Comparison</h2>
        <p>A horizontal bar chart comparing the top 5 departments (sorted by completion percentage) across two metrics:</p>
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Completion %</strong></td>
              <td>The percentage of assigned courses that have been completed by department members.</td>
            </tr>
            <tr>
              <td><strong>Avg Progress %</strong></td>
              <td>The average progress percentage across all in-progress course assignments for the department.</td>
            </tr>
          </tbody>
        </table>
        <p>Hover over any bar to see the full department name and exact values in a tooltip.</p>
      </section>

      <section>
        <h2>Department-wise Assignment Skills Matrix</h2>
        <p>A radar chart displaying up to 6 departments across three dimensions:</p>
        <table>
          <thead>
            <tr>
              <th>Dimension</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Completion %</strong></td>
              <td>Course completion rate for the department.</td>
            </tr>
            <tr>
              <td><strong>Engagement %</strong></td>
              <td>Average of completion rate and progress — an indicator of how actively the department is engaged.</td>
            </tr>
            <tr>
              <td><strong>Progress %</strong></td>
              <td>Average progress across all assignments for the department.</td>
            </tr>
          </tbody>
        </table>
        <p>This chart helps identify departments that are strong across all dimensions versus those that may need additional support.</p>
      </section>

      <section>
        <h2>Learning Progress Tracker</h2>
        <p>A line chart tracking assignment status trends over time. Use the toggle buttons above the chart to switch between <strong>Courses</strong>, <strong>Certifications</strong>, or <strong>Learning Paths</strong>.</p>
        <table>
          <thead>
            <tr>
              <th>Line</th>
              <th>Color</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Not Started</strong></td>
              <td><span style="color:#f59e0b;">Amber</span></td>
              <td>Count of assignments not yet started for the selected period.</td>
            </tr>
            <tr>
              <td><strong>In Progress</strong></td>
              <td><span style="color:#3b82f6;">Blue</span></td>
              <td>Count of assignments currently in progress.</td>
            </tr>
            <tr>
              <td><strong>Completed</strong></td>
              <td><span style="color:#10b981;">Green</span></td>
              <td>Count of completed assignments.</td>
            </tr>
          </tbody>
        </table>
        <p>Summary pills above the chart display total counts for each status. Hover over data points for exact values.</p>
      </section>

      <section>
        <h2>Certificates Earned</h2>
        <p>A radar chart showing the distribution of certificates earned across three categories:</p>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Color</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Courses</strong></td>
              <td><span style="color:#6366f1;">Indigo</span></td>
              <td>Certificates earned from course completions.</td>
            </tr>
            <tr>
              <td><strong>Certifications</strong></td>
              <td><span style="color:#f59e0b;">Amber</span></td>
              <td>Certificates earned from certification exams.</td>
            </tr>
            <tr>
              <td><strong>Learning Paths</strong></td>
              <td><span style="color:#10b981;">Green</span></td>
              <td>Certificates earned from learning path completions.</td>
            </tr>
          </tbody>
        </table>
        <p>Total counts for each category are displayed above the chart.</p>
      </section>

      <section>
        <h2>Activity Heatmap</h2>
        <p>A grid of the top 6 activity event types for the selected period. Each tile shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Count</strong></td>
              <td>Total number of events of that type.</td>
            </tr>
            <tr>
              <td><strong>Percentage</strong></td>
              <td>Share of that event type relative to all events, shown as a badge and a progress bar.</td>
            </tr>
            <tr>
              <td><strong>Color intensity</strong></td>
              <td>The tile's background gradient intensity reflects the event's proportion — higher percentage means more intense color.</td>
            </tr>
          </tbody>
        </table>
        <p>Hover over a tile to see the exact event count in a tooltip.</p>
      </section>

      <section>
        <h2>Activity Timeline</h2>
        <p>A vertical timeline showing the 5 most recent learning events across the platform. Each entry includes:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Status indicator</strong></td>
              <td>Color-coded circle — <span style="color:#10b981;">green</span> for success, <span style="color:#f59e0b;">yellow</span> for warning, <span style="color:#ef4444;">red</span> for error, <span style="color:#3b82f6;">blue</span> for info.</td>
            </tr>
            <tr>
              <td><strong>Action</strong></td>
              <td>Description of the activity (e.g., course enrollment, completion, certification award).</td>
            </tr>
            <tr>
              <td><strong>Details</strong></td>
              <td>Additional context about the activity.</td>
            </tr>
            <tr>
              <td><strong>User & Timestamp</strong></td>
              <td>Who performed the action and when (formatted as DD-MM-YYYY hh:mm AM/PM).</td>
            </tr>
          </tbody>
        </table>
        <p>Click <strong>View All Activity →</strong> to navigate to the full activity reports page.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Daily admin check-in</div>
            <div class="sz-use-case-flow">
              Open the dashboard each morning to review stat card changes and the activity timeline for overnight events. Quickly spot departments falling behind using the performance bar chart.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Executive reporting</div>
            <div class="sz-use-case-flow">
              Select the desired time range (quarter or custom) before leadership meetings. Use the stats cards for headline numbers and the radar chart to compare department engagement across dimensions.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department performance review</div>
            <div class="sz-use-case-flow">
              Use the Department Performance Comparison chart to identify top and bottom performing departments. Cross-reference with the Skills Matrix radar to understand whether low completion is due to low engagement or low progress.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Use the <strong>Custom</strong> date range to compare specific training periods (e.g., before and after a new course rollout) by sharing the URL with stakeholders.</p>
      </div>

    </div>`,
  },
};
